<script>
   /*************************************************************/
   /* Root component for the application                        */
   /*************************************************************/

   import { date2str } from '@mdatools-apps/shared/utils';
   import { checkPrepField, checkPrepChain } from '@mdatools-apps/shared/methods/prep.js';

   import TrainAppContainer from './tab_train/AppContainer.svelte';
   import TestAppContainer from './tab_test/AppContainer.svelte';
   import NewAppContainer from './tab_new/AppContainer.svelte';

   import App from '@mdatools-apps/shared/components/MainApp.svelte';
   import WASM from '@mdatools-apps/wasm/pls_wasm.js';


   /* callback to check validity of JSON with session */
   function checkSession(s) {
      const keys1 = Object.keys(s);
      if (!['date', 'tab', 'train', 'test', 'new'].every(v => keys1.includes(v))) return false;

      const keys2_expected = ['model', 'calset', 'calsetp', 'calres', 'pvset', 'pvres', 'ind',
         'relabeled', 'outliers', 'exclvars', 'ncomp_selected', 'pvscalars', 'prepType', 'prepmodel',
         'prepmeta', 'prepobj'];
      const keys2 = Object.keys(s.train);
      if (!keys2_expected.every(v => keys2.includes(v))) return false;

      const keys3_expected = ['model', 'testset', 'testres', 'relabeled', 'outliers'];
      const keys3 = Object.keys(s.test);
      if (!keys3_expected.every(v => keys3.includes(v))) return false;

      const keys4_expected = ['model', 'newset', 'newres', 'outliers'];
      const keys4 = Object.keys(s.new);
      if (!keys4_expected.every(v => keys4.includes(v))) return false;

      // the keys above are required and not their values: 0 is what 'initSession' writes and null
      // what a loader writes for 'nothing yet'. the shape is what the tabs rely on: a container of
      // a model or of results is read as 'x.pls' and 'x.da' with no guard on the container, and a
      // list as an array
      const isObject = v => v !== null && typeof v === 'object';
      const container = v => isObject(v) && 'pls' in v && 'da' in v;
      if (![s.train.model, s.train.calres, s.train.pvres, s.test.testres, s.new.newres]
            .every(container)) return false;
      if (![s.train.outliers, s.train.relabeled, s.train.exclvars, s.train.prepobj,
            s.test.outliers, s.test.relabeled, s.new.outliers].every(Array.isArray)) return false;

      // a stored preprocessing model is rendered as soon as its tab mounts, and the chain read by
      // the creator, where no catch reaches: the checks a file passes (prep's 'checkSession' runs
      // the same); 0 and null are none
      try {
         checkPrepChain(s.train.prepobj);
         for (const p of [s.train.model.pls?.prep, s.train.prepmodel, s.test.model?.pls?.prep,
                         s.new.model?.pls?.prep]) checkPrepField(p);
      } catch { return false; }

      // a class named 'total' takes the key 'predict' keeps the totals under, so the results table
      // throws on it; both loaders refuse the name, a session stored before they did carries it
      const hasTotal = v => isObject(v) && Array.isArray(v.classNames) &&
         v.classNames.includes('total');
      if ([s.train.calset, s.train.model.da, s.test.model?.da, s.new.model?.da].some(hasTotal)) {
         return false;
      }

      // fields an earlier build stored and this one does not read: the statistics matrices are
      // derived in the tabs, 'damodel' was never read, 'ncomp_selected' is the train tab's alone.
      // stripped here, the one place both restore paths pass, or a session from that build would
      // carry them in every backup
      delete s.train.statmat;
      delete s.test.statmat;
      delete s.test.damodel;
      delete s.test.ncomp_selected;
      delete s.new.damodel;
      delete s.new.ncomp_selected;

      return true;
   }

   /* callback to initialize new session */
   function initSession() {
      return {
         date: date2str(new Date()),
         tab: 0,
         train: {
            model: {pls: 0, da: 0},
            calset: 0,
            calsetp: 0,
            pvset: 0,
            ind: 0,
            calres: {pls: 0, da: 0},
            pvres: {pls: 0, da: 0},
            relabeled: [],
            outliers: [],
            exclvars: [],
            ncomp_selected: 1,
            pvscalars: 0,
            prepType: null,
            prepmodel: 0,
            prepmeta: 0,
            prepobj: [],
         },
         test: {
            model : 0,
            testset: 0,
            testres: {pls: 0, da: 0},
            ind: 0,
            relabeled: [],
            outliers: [],
         },
         new: {
            model : 0,
            newset: 0,
            newres: {pls: 0, da: 0},
            outliers: [],
         }
      }
   }

   // app containers
   const tabs = [
      {name: 'Train', label: 'train', component: TrainAppContainer},
      {name: 'Test', label: 'test', component: TestAppContainer},
      {name: 'Predict', label: 'new', component: NewAppContainer},
   ]
</script>

<App
   appID="plsda"
   appTitle="PLS1-DA"
   appShortTitle="PLS1-DA"
   appSlogan="PLS1 Discriminant Analysis (binary)"
   {WASM}
   {tabs}
   {initSession}
   {checkSession}
>

   {#snippet footer()}
      <p>Current release: <span>1.7.2</span>
      Feedback: <a class="paper-link" href="mailto:help@mda.tools?subject=PLS1-DA app">help@mda.tools</a>
      Tutorial: <a class="paper-link" href="Olives.zip" download>dataset</a>
      | <a href="https://www.youtube.com/playlist?list=PLep7KM9cEyFnqbuRD1dxZD6ePgTmg5lP8">video lessons</a>
      </p>
   {/snippet}

   {#snippet info()}
   <h2>PLS1 Discriminant Analysis (binary version)</h2>
   <p>
      Interactive web-application for training, testing and applying PLS-DA models for
      discriminanation between two classes (binary version). The app implements both conventional
      method based on threshold (hard) as well as soft method based on distributions (soft),
      described in <a href="https://doi.org/10.1016/j.microc.2025.113329">this paper</a>.
   </p>
   <p>
      The application uses <a href="https://doi.org/10.1016/j.aca.2023.341096">Procrustes cross-validation</a> for optimization and quality check on the training stage.
   </p>
   <p>Check out YouTube <a href="https://www.youtube.com/playlist?list=PLep7KM9cEyFnqbuRD1dxZD6ePgTmg5lP8">video-lessons</a> for more details. The lessons are based on dataset Olives, the description can be found in the following papers: <a href="https://doi.org/10.1016/j.aca.2014.09.013">10.1016/j.aca.2014.09.013</a>, <a href="https://doi.org/10.1016/j.chemolab.2016.10.002">10.1016/j.chemolab.2016.10.002</a>. Authors are grateful to Paolo Oliveri for giving us permision of using this dataset.
   </p>
   <h3>v. 1.7.2</h3>
   <p class="release-info">released 01/11/2026</p>
   <ul>
      <li>New higher-contrast theme for the control elements.</li>
      <li>Improved accessibility of the controls.</li>
      <li>Added possibility to save plot data as CSV file.</li>
      <li>New more efficient and faster session system.</li>
      <li>Small bug fixes and improvements.</li>
   </ul>
   {/snippet}
</App>

